import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  loadAboutPage,
  loadServicesPage,
  loadProjectsPage,
  loadContactPage,
  loadDreamsPage,
} from "@/lib/pageLoaders";
import { useCompanyInfo } from "@/hooks/useCompanyInfo";

const FALLBACK_LOGO = "/images/1-logo/yatay/yatay2.png";

const HERO_IMAGES = {
  "/hakkimizda": "/images/3-hakkimizda/hakkimizda_opt.webp",
  "/hizmetler": "/images/4-hizmetler/hizmetler_opt.webp",
  "/iletisim": "/images/6-iletisim/iletisim_opt.webp",
  "/dreams": "/images/7-dreams/dreams_opt.webp",
};

const preloadedSet = new Set();

const preloadHeroImage = (path) => {
  const src = HERO_IMAGES[path];
  if (!src || preloadedSet.has(src)) return;
  preloadedSet.add(src);
  const img = new window.Image();
  img.fetchPriority = "high";
  img.src = src;
};

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { logo_url } = useCompanyInfo();
  const logoSrc = logo_url?.trim() || FALLBACK_LOGO;

  useEffect(() => {
    let rafId = null;

    const handleScroll = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        const nextScrolled = window.scrollY > 50;
        setScrolled((prev) => (prev === nextScrolled ? prev : nextScrolled));
        rafId = null;
      });
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  const navLinks = [
    { name: "Ana Sayfa", path: "/" },
    { name: "Hakkımızda", path: "/hakkimizda" },
    { name: "Hizmetler", path: "/hizmetler" },
    { name: "Projeler", path: "/projeler" },
    { name: "İletişim", path: "/iletisim" },
  ];

  const preloadByPath = {
    "/hakkimizda": loadAboutPage,
    "/hizmetler": loadServicesPage,
    "/projeler": loadProjectsPage,
    "/iletisim": loadContactPage,
    "/dreams": loadDreamsPage,
  };

  const handlePreload = (path) => {
    preloadByPath[path]?.();
    preloadHeroImage(path);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-[2000] transition-colors duration-300 ${
          scrolled ? "glassmorphic" : "bg-transparent"
        }`}
        data-testid="main-header"
      >
        <nav className="max-w-[1400px] mx-auto px-6 md:px-12 py-[6px] md:py-0 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img
              src={logoSrc}
              alt="Ober Yapı Konya İnşaat"
              className="h-[84px] md:h-[120px] w-auto object-contain"
              style={{
                filter:
                  "drop-shadow(0 1px 6px rgba(0,0,0,0.85)) drop-shadow(0 0px 16px rgba(0,0,0,0.6)) drop-shadow(0 0px 3px rgba(0,0,0,0.9))",
              }}
            />
          </Link>

          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onMouseEnter={() => handlePreload(link.path)}
                onFocus={() => handlePreload(link.path)}
                data-testid={`nav-${link.name.toLowerCase().replace(" ", "-")}`}
                className={`text-base font-light tracking-wide transition-colors duration-200 ${
                  location.pathname === link.path
                    ? "text-[#C5A059]"
                    : "text-[#F5F5F7] hover:text-[#C5A059]"
                }`}
                style={{
                  textShadow:
                    "0 1px 4px rgba(0,0,0,0.8), 0 0 10px rgba(0,0,0,0.5)",
                }}
              >
                {link.name}
              </Link>
            ))}
            <Link
              to="/dreams"
              onMouseEnter={() => handlePreload("/dreams")}
              onFocus={() => handlePreload("/dreams")}
              data-testid="nav-dreams"
              className="signature-container relative group overflow-visible py-1"
            >
              <span
                className={`font-signature text-2xl signature-glow transition-all duration-300 inline-block ${
                  location.pathname === "/dreams"
                    ? "signature-gold"
                    : "signature-gold opacity-80 group-hover:opacity-100"
                }`}
              >
                Dreams
              </span>
            </Link>
          </div>

          <Link
            to="/iletisim"
            onMouseEnter={() => handlePreload("/iletisim")}
            onFocus={() => handlePreload("/iletisim")}
            data-testid="header-cta-button"
            className="hidden lg:block bg-[#C5A059] hover:bg-[#D4AF37] text-black px-7 py-3 text-sm font-medium transition-all duration-200 hover:scale-105"
          >
            İletişime Geç
          </Link>

          <button
            onClick={() => setMobileMenuOpen(true)}
            className="lg:hidden text-[#F5F5F7] p-2"
            data-testid="mobile-menu-toggle"
            aria-label="Menüyü aç"
          >
            <Menu size={24} />
          </button>
        </nav>
      </header>

      {/* Mobile menu — sibling of header, no stacking context issue */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            data-testid="mobile-menu"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 9999,
              backgroundColor: "#0a0a0a",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Altın üst çizgi */}
            <div
              style={{
                height: 2,
                background: "linear-gradient(90deg, #C5A059, #D4AF37, #C5A059)",
              }}
            />

            {/* Logo + Kapat */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "14px 24px",
                borderBottom: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <Link to="/" onClick={() => setMobileMenuOpen(false)}>
                <img
                  src={logoSrc}
                  alt="Ober Yapı Konya İnşaat"
                  style={{ height: 44, width: "auto", objectFit: "contain" }}
                />
              </Link>
              <button
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Menüyü kapat"
                style={{
                  color: "#F5F5F7",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Linkler */}
            <div style={{ padding: "4px 24px" }}>
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(" ", "-")}`}
                  style={{
                    display: "block",
                    padding: "13px 0",
                    borderBottom: "1px solid rgba(255,255,255,0.07)",
                    fontSize: 16,
                    fontWeight: 300,
                    letterSpacing: "0.06em",
                    color:
                      location.pathname === link.path ? "#C5A059" : "#E8E8E8",
                    textDecoration: "none",
                  }}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/dreams"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-dreams"
                style={{
                  display: "block",
                  padding: "13px 0",
                  textDecoration: "none",
                }}
              >
                <span className="font-signature text-2xl signature-gold signature-glow">
                  Dreams
                </span>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
